/**
 * BatchSimulation - Multi-hand simulation with failure-mode analysis
 *
 * Runs the shared hand-sim engine across hundreds of opening hands and reports
 * the land distribution, a breakdown of *why* hands fail, and the run-over-run
 * change in keep rate so mana base edits can be judged statistically.
 */
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { IoPlay } from 'react-icons/io5';

import InfoButton from '@/components/InfoButton/InfoButton';
import type { BatchResults, HandIssueType, KeepRules } from '@/features/decks/lib/handSim';
import { buildLibrary, isKeepRateChangeSignificant, keepRateMargin, runBatch } from '@/features/decks/lib/handSim';

import type { EnrichedDeckCard } from '../../types';

import BatchSimulationInfo from './BatchSimulationInfo';
import FirstPlayDistribution from './FirstPlayDistribution';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ============================================================================
// Types
// ============================================================================

export interface BatchSimulationProps {
  cards: EnrichedDeckCard[];
  rules: KeepRules;
  /** Turn 1 skips the draw step in the goldfish turn model */
  onThePlay: boolean;
  className?: string;
}

const HAND_SIZE = 7;

/**
 * Fixed sample size. Runtime is ~220ms on a 100-card deck, so the only thing a
 * smaller run would buy is a wider error bar — at 10k the keep rate is accurate
 * to about ±1%, which is what makes the run-over-run delta trustworthy.
 */
const HANDS_PER_RUN = 10_000;

const FAILURE_META: { type: HandIssueType; label: string; bar: string; text: string }[] = [
  { type: 'screw', label: 'screw', bar: 'bg-rose-500', text: 'text-rose-400' },
  { type: 'flood', label: 'flood', bar: 'bg-orange-500', text: 'text-orange-400' },
  { type: 'no-spells', label: 'all lands', bar: 'bg-fuchsia-500', text: 'text-fuchsia-400' },
  { type: 'color-screw', label: 'color', bar: 'bg-sky-500', text: 'text-sky-400' },
  { type: 'slow-start', label: 'slow', bar: 'bg-amber-500', text: 'text-amber-400' },
];

// ============================================================================
// Component
// ============================================================================

export default function BatchSimulation({ cards, rules, onThePlay, className }: BatchSimulationProps): ReactElement {
  const [results, setResults] = useState<BatchResults | null>(null);
  const [previous, setPrevious] = useState<BatchResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const library = useMemo(() => buildLibrary(cards), [cards]);

  const landCount = library.filter((card) => card.isLand).length;
  const landPercentage = library.length > 0 ? (landCount / library.length) * 100 : 0;

  const keepRate = results ? results.keepableHands / results.totalHands : null;

  const handleRun = (): void => {
    if (library.length < HAND_SIZE) return;

    setIsRunning(true);
    // Yield a frame so the "Running..." state paints before the loop blocks.
    setTimeout(() => {
      setPrevious(results);
      setResults(runBatch(library, HAND_SIZE, HANDS_PER_RUN, rules, onThePlay));
      setIsRunning(false);
    }, 10);
  };

  // Chart data — grouped bars: raw lands vs effective
  const chartData = useMemo(
    () => ({
      labels: ['0', '1', '2', '3', '4', '5', '6', '7'],
      datasets: [
        {
          label: 'Lands only',
          data: results?.landDistribution ?? [0, 0, 0, 0, 0, 0, 0, 0],
          backgroundColor: 'rgba(99, 102, 241, 0.5)',
          borderRadius: 2,
          barPercentage: 0.85,
          categoryPercentage: 0.7,
        },
        {
          label: 'With rocks',
          data: results?.effectiveDistribution ?? [0, 0, 0, 0, 0, 0, 0, 0],
          backgroundColor: results?.effectiveDistribution.map((_, i) =>
            i >= rules.minSources && i <= rules.maxLands ? 'rgba(34, 197, 94, 0.7)' : 'rgba(251, 191, 36, 0.5)',
          ),
          borderRadius: 2,
          barPercentage: 0.85,
          categoryPercentage: 0.7,
        },
      ],
    }),
    [results, rules.minSources, rules.maxLands],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1a1a1f',
          borderColor: '#2a2a32',
          borderWidth: 1,
          titleColor: '#e8e8ef',
          bodyColor: '#a8a8b8',
          padding: 8,
          displayColors: true,
          callbacks: {
            title: (items: { label: string }[]) => `${items[0].label} Mana Sources`,
            label: (item: { raw: unknown; dataset: { label?: string } }) => {
              const count = Number(item.raw);
              const pct = results ? ((count / results.totalHands) * 100).toFixed(1) : '0';
              return `${item.dataset.label}: ${count} hands (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#6b6b7a', font: { size: 11 } },
          border: { display: false },
          title: {
            display: true,
            text: 'Mana Sources in Hand',
            color: '#6b6b7a',
            font: { size: 10 },
          },
        },
        y: {
          grid: { color: '#2a2a32', drawTicks: false },
          ticks: { color: '#6b6b7a', font: { size: 10 } },
          border: { display: false },
          beginAtZero: true,
        },
      },
    }),
    [results],
  );

  // Not enough cards
  if (library.length < HAND_SIZE) {
    return (
      <div className={clsx('space-y-3', className)}>
        <div className="flex items-center gap-1">
          <span className="text-ash text-xs font-medium">Batch Simulation</span>
          <InfoButton title="Batch Simulation">
            <BatchSimulationInfo />
          </InfoButton>
        </div>
        <div className="rounded-lg bg-zinc-800/30 p-4 text-center">
          <p className="text-ash text-sm">Need at least 7 main-deck cards</p>
          <p className="text-ash/60 mt-1 text-xs">Commanders, companions and sideboard cards are never shuffled into the library</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-ash text-xs font-medium">Batch Simulation</span>
          <InfoButton title="Batch Simulation">
            <BatchSimulationInfo />
          </InfoButton>
        </div>
        <div className="text-ash text-xs">
          {landPercentage.toFixed(0)}% lands ({landCount}/{library.length})
        </div>
      </div>

      {/* Simulation controls */}
      <div className="flex items-center gap-2">
        <span className="text-ash flex-1 text-xs">
          {HANDS_PER_RUN.toLocaleString()} hands per run · {onThePlay ? 'on the play' : 'on the draw'}
        </span>
        <button
          type="button"
          onClick={handleRun}
          disabled={isRunning}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
            isRunning ? 'cursor-not-allowed bg-zinc-800/50 text-zinc-500' : 'bg-aether hover:bg-aether/80 text-white',
          )}
        >
          <IoPlay className="h-3.5 w-3.5" />
          {isRunning ? 'Running...' : results ? 'Re-run' : 'Run'}
        </button>
      </div>

      {!results && !isRunning && (
        <p className="text-ash/70 text-xs">Run a batch to see how often this deck offers a keepable opening hand.</p>
      )}

      {/* Results */}
      {results && keepRate !== null && (
        <>
          {/* Stats summary */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-zinc-800/40 px-3 py-2 text-center">
              <div className="text-pearl text-lg font-bold">
                {(keepRate * 100).toFixed(0)}%
                <span className="text-ash ml-1 text-[10px] font-normal">±{(keepRateMargin(results) * 100).toFixed(1)}</span>
              </div>
              <div className="text-ash text-[10px]">Keepable</div>
              <KeepRateDelta previous={previous} current={results} />
            </div>
            <div className="rounded-lg bg-zinc-800/40 px-3 py-2 text-center">
              <div className="text-silver text-lg font-bold">
                {results.averageFirstPlayTurn === null ? '—' : `T${results.averageFirstPlayTurn.toFixed(1)}`}
              </div>
              <div className="text-ash text-[10px]">Avg First Play</div>
              <div className="text-ash/60 text-[10px]">wants T{rules.slowStartTurn}</div>
            </div>
            <div className="rounded-lg bg-zinc-800/40 px-3 py-2 text-center">
              <div className="text-silver text-lg font-bold">{results.averageEffective.toFixed(2)}</div>
              <div className="text-ash text-[10px]">Avg Effective</div>
            </div>
            <div className="rounded-lg bg-zinc-800/40 px-3 py-2 text-center">
              <div className="text-silver text-lg font-bold">{results.averageLands.toFixed(2)}</div>
              <div className="text-ash text-[10px]">Avg Lands</div>
            </div>
          </div>

          {/* Failure-mode breakdown */}
          <FailureBreakdown results={results} />

          {/* Goldfish first-play spread */}
          <FirstPlayDistribution results={results} />

          {/* Distribution chart */}
          <div className="h-32">
            <Bar data={chartData} options={chartOptions} />
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm bg-indigo-500/50" />
              <span className="text-ash">Lands only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm bg-emerald-500/70" />
              <span className="text-ash">With rocks (in range)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm bg-amber-400/50" />
              <span className="text-ash">With rocks (out of range)</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Failure breakdown
// ============================================================================

function FailureBreakdown({ results }: { results: BatchResults }): ReactElement {
  const failures = FAILURE_META.map((meta) => ({ ...meta, count: results.failureCounts[meta.type] })).filter((entry) => entry.count > 0);

  const unkeepable = results.totalHands - results.keepableHands;
  const pct = (count: number): number => (count / results.totalHands) * 100;

  if (unkeepable === 0) {
    return <p className="text-xs text-emerald-400">Every simulated hand met the keep rules.</p>;
  }

  return (
    <div className="space-y-1.5">
      <div className="flex h-2 overflow-hidden rounded-full bg-zinc-800">
        <div className="bg-emerald-500" style={{ width: `${pct(results.keepableHands)}%` }} />
        {failures.map((entry) => (
          <div key={entry.type} className={entry.bar} style={{ width: `${pct(entry.count)}%` }} />
        ))}
      </div>
      <p className="text-ash text-[11px]">
        <span className="text-pearl font-medium">{pct(unkeepable).toFixed(0)}% unkeepable</span>
        {' — '}
        {failures.map((entry, index) => (
          <span key={entry.type}>
            {index > 0 && ' · '}
            <span className={entry.text}>
              {pct(entry.count).toFixed(0)}% {entry.label}
            </span>
          </span>
        ))}
      </p>
    </div>
  );
}

// ============================================================================
// Run-over-run delta
// ============================================================================

function KeepRateDelta({ previous, current }: { previous: BatchResults | null; current: BatchResults }): ReactElement {
  if (!previous) return <div className="text-ash/60 text-[10px]">first run</div>;

  const before = previous.keepableHands / previous.totalHands;
  const after = current.keepableHands / current.totalHands;
  const change = after - before;

  if (!isKeepRateChangeSignificant(previous, current)) {
    return <div className="text-ash/60 text-[10px]">no real change</div>;
  }

  return (
    <div className={clsx('text-[10px]', change >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
      {(before * 100).toFixed(0)}% → {(after * 100).toFixed(0)}% ({change >= 0 ? '+' : ''}
      {(change * 100).toFixed(1)})
    </div>
  );
}
